import type { Edge, Node } from '@xyflow/react';
import type { ListStepTypesResponse } from '@erp/rpc';
import {
  archiveShipAndDebitAzureFilesStep,
  backgroundAgentStep,
  blobStorageFetchStep,
  classifyDocumentV2Step,
  classifyEmailStep,
  classifyMasterDataStep,
  confidenceThresholdStep,
  coreEnumFanoutStep,
  decisionStep,
  emailRuleTriggerStep,
  eventTriggerStep,
  actionInputStep,
  actionOutputStep,
  FLOW_CONTROL_SOCKET_TYPE,
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  logValueStep,
  pipelineRowsInsertStep,
  pipelineRowsQueryStep,
  pipelineRowsUpdateStep,
  randomNumberGeneratorStep,
  scheduleTriggerStep,
  workflowActionStep,
  codeSandboxStep,
  statefulTableLookupRowStep,
  statefulTableReadFrameStep,
  managedMapperStep,
  transformE844dFieldsStep,
  fwfDataSourceWriterStep,
  csvDataSourceWriterStep,
  type ValidationEdge,
  type ValidationStep,
} from '@erp/workflows';
import { IOType } from '@erp/shared-types/node-graph';

/**
 * Step types whose sockets are derived from `config` rather than from a
 * fixed Zod schema. Used in:
 *   - WorkflowEditor's `onConfigChange` → regenerate sockets when config changes
 *   - graphToDefinition → persist live sockets on save (instead of the
 *     stale ones that came from the last server response)
 *   - `hydrateStepTypeData` → preserve actual node sockets when re-hydrating
 *     `_stepTypeData` from the API metadata
 *
 * Keep in sync with `hasDynamicOutputSockets`/dispatchers in
 * `backend/src/workflows/sockets/dynamicOutputSockets.ts`.
 */
export type DynamicSocketBehavior = {
  dynamicInputs: boolean;
  dynamicOutputs: boolean;
};

export const DYNAMIC_SOCKET_BEHAVIORS: Record<string, DynamicSocketBehavior> = {
  ai_reasoning: { dynamicInputs: false, dynamicOutputs: true },
  event_trigger: { dynamicInputs: false, dynamicOutputs: true },
  wait_for_event: { dynamicInputs: false, dynamicOutputs: true },
  background_agent: { dynamicInputs: true, dynamicOutputs: true },
  switch: { dynamicInputs: false, dynamicOutputs: true },
  interpolate_email_template: { dynamicInputs: true, dynamicOutputs: false },
  split_document_v2: { dynamicInputs: false, dynamicOutputs: true },
  identify_customer: { dynamicInputs: false, dynamicOutputs: true },
  action_input: { dynamicInputs: false, dynamicOutputs: true },
  action_output: { dynamicInputs: true, dynamicOutputs: true },
  workflow_action: { dynamicInputs: true, dynamicOutputs: true },
  code_sandbox: { dynamicInputs: true, dynamicOutputs: true },
  entity_resolver: { dynamicInputs: true, dynamicOutputs: true },
  approval: { dynamicInputs: true, dynamicOutputs: true },
  extract_document_content_v3: { dynamicInputs: false, dynamicOutputs: true },
  extract_document_content_v4: { dynamicInputs: false, dynamicOutputs: true },
  http_trigger: { dynamicInputs: false, dynamicOutputs: true },
  stateful_table_lookup_row: { dynamicInputs: false, dynamicOutputs: true },
  stateful_table_read_frame: { dynamicInputs: true, dynamicOutputs: false },
  external_http_call: { dynamicInputs: true, dynamicOutputs: true },
  voice_agent: { dynamicInputs: false, dynamicOutputs: true },
  managed_mapper: { dynamicInputs: true, dynamicOutputs: true },
  web_browser: { dynamicInputs: true, dynamicOutputs: false },
};

export const STEP_CONFIG_SCHEMAS: Record<
  string,
  {
    safeParse: (data: unknown) => {
      success: boolean;
      data?: unknown;
      error?: { issues: Array<{ path: PropertyKey[]; message: string }> };
    };
  }
> = {
  event_trigger: eventTriggerStep.configSchema,
  email_rule_trigger: emailRuleTriggerStep.configSchema,
  action_input: actionInputStep.configSchema,
  action_output: actionOutputStep.configSchema,
  workflow_action: workflowActionStep.configSchema,
  schedule_trigger: scheduleTriggerStep.configSchema,
  background_agent: backgroundAgentStep.configSchema,
  confidence_threshold: confidenceThresholdStep.configSchema,
  decision: decisionStep.configSchema,
  blob_storage_fetch: blobStorageFetchStep.configSchema,
  core_enum_fanout: coreEnumFanoutStep.configSchema,
  pipeline_rows_query: pipelineRowsQueryStep.configSchema,
  pipeline_rows_update: pipelineRowsUpdateStep.configSchema,
  pipeline_rows_insert: pipelineRowsInsertStep.configSchema,
  random_number_generator: randomNumberGeneratorStep.configSchema,
  log_value: logValueStep.configSchema,
  classify_email: classifyEmailStep.configSchema,
  classify_document_v2: classifyDocumentV2Step.configSchema,
  classify_master_data: classifyMasterDataStep.configSchema,
  archive_ship_and_debit_azure_files:
    archiveShipAndDebitAzureFilesStep.configSchema,
  transform_e844d_fields: transformE844dFieldsStep.configSchema,
  code_sandbox: codeSandboxStep.configSchema,
  stateful_table_lookup_row: statefulTableLookupRowStep.configSchema,
  stateful_table_read_frame: statefulTableReadFrameStep.configSchema,
  managed_mapper: managedMapperStep.configSchema,
  fwf_datasource_writer: fwfDataSourceWriterStep.configSchema,
  csv_datasource_writer: csvDataSourceWriterStep.configSchema,
};

export function getSocketTypeFromNode(
  node: Node,
  handleId: string | null | undefined,
  direction: 'input' | 'output',
): string {
  if (!handleId) return 'any';

  if (handleId === FLOW_IN_SOCKET_ID || handleId === FLOW_OUT_SOCKET_ID) {
    return FLOW_CONTROL_SOCKET_TYPE;
  }

  const nodeData = node.data as {
    inputs?: Record<string, { id: string; socketType?: string }>;
    outputs?: Record<string, { id: string; socketType?: string }>;
  };

  const sockets = direction === 'input' ? nodeData.inputs : nodeData.outputs;
  if (!sockets) return 'any';

  const socket = Object.values(sockets).find((entry) => entry.id === handleId);
  return socket?.socketType || 'any';
}

export function mapSockets(
  sockets: Array<{
    id: string;
    name: string;
    type: string;
    required?: boolean;
  }>,
  ioType: IOType,
): Record<
  string,
  { id: string; ioType: IOType; socketType: string; required?: boolean }
> {
  return Object.fromEntries(
    sockets.map((socket) => [
      socket.name,
      {
        id: socket.id,
        ioType,
        socketType: socket.type,
        required: socket.required,
      },
    ]),
  );
}

/**
 * Socket record entry as stored on editor node data (`data.inputs` /
 * `data.outputs`), produced by `mapSockets`.
 */
export interface NodeSocketRecordEntry {
  id: string;
  ioType: IOType | number;
  socketType?: string;
  required?: boolean;
}

type StepTypeData = ListStepTypesResponse['stepTypes'][0];

/**
 * Hydrate step-type metadata for a node, preserving the node's actual
 * (DB-stored) sockets for steps with dynamic sockets instead of the base
 * schema sockets from the API.
 *
 * The `required` flag must come from the stored socket: dynamic input
 * sockets are frequently optional (e.g. stateful_table_read_frame's
 * per-column filters are ALL optional). Hardcoding `required: true` here
 * made the editor render optional inputs with a required marker and —
 * because `nodesToValidationSteps` reads these hydrated sockets — blocked
 * Save & Publish with "missing required inputs" for any read-frame node
 * with unwired filters.
 */
export function hydrateDynamicStepTypeData(
  stData: StepTypeData,
  stepType: string,
  inputs: Record<string, NodeSocketRecordEntry>,
  outputs: Record<string, NodeSocketRecordEntry>,
): StepTypeData {
  const behavior = DYNAMIC_SOCKET_BEHAVIORS[stepType];
  if (!behavior) return stData;

  const nodeInputSockets = Object.entries(inputs);
  const nodeOutputSockets = Object.entries(outputs);

  const hydrated: StepTypeData = { ...stData };

  if (behavior.dynamicInputs && nodeInputSockets.length > 0) {
    hydrated.inputSockets = nodeInputSockets.map(([name, socket]) => ({
      id: socket.id,
      name,
      type: socket.socketType ?? 'any',
      required: socket.required ?? true,
    }));
  }

  if (behavior.dynamicOutputs && nodeOutputSockets.length > 0) {
    hydrated.outputSockets = nodeOutputSockets.map(([name, socket]) => ({
      id: socket.id,
      name,
      type: socket.socketType ?? 'any',
      required: true,
    }));
  }

  return hydrated;
}

export function nodesToValidationSteps(nodes: Node[]): ValidationStep[] {
  return nodes.map((node) => {
    const data = node.data as {
      stepType?: string;
      category?: string;
      inputs?: Record<string, { id: string; required?: boolean }>;
      outputs?: Record<string, { id: string }>;
      _stepTypeData?: ListStepTypesResponse['stepTypes'][0];
    };

    return {
      id: node.id,
      type: data.stepType ?? 'unknown',
      category:
        (data.category as ValidationStep['category']) ??
        data._stepTypeData?.category ??
        'Utility',
      config: (data as Record<string, unknown>).config as
        | Record<string, unknown>
        | undefined,
      sockets: {
        inputs: data._stepTypeData
          ? data._stepTypeData.inputSockets.map((socket) => ({
              id: socket.id,
              name: socket.name,
              required: socket.required ?? true,
            }))
          : data.inputs
            ? Object.values(data.inputs).map((socket) => ({
                id: socket.id,
                name: socket.id,
                required: socket.required ?? true,
              }))
            : [],
        outputs: data._stepTypeData
          ? data._stepTypeData.outputSockets.map((socket) => ({
              id: socket.id,
              name: socket.name,
            }))
          : data.outputs
            ? Object.values(data.outputs).map((socket) => ({
                id: socket.id,
                name: socket.id,
              }))
            : [],
      },
    };
  });
}

export function edgesToValidationEdges(edges: Edge[]): ValidationEdge[] {
  return edges.map((edge) => ({
    id: String(edge.id),
    sourceStepId: edge.source,
    sourceSocketId: edge.sourceHandle ?? '',
    targetStepId: edge.target,
    targetSocketId: edge.targetHandle ?? '',
  }));
}
